import React from 'react';
import { 
  Search, Phone, MessageSquare, Mail, Linkedin, RotateCcw,
  Building2, User, Globe, CheckCircle, Clock, 
  Eye, Send, Calendar, MapPin, Star, Briefcase,
  Users, Target, PhoneCall, MessageCircle, UserCheck,
  CalendarCheck, Zap, ArrowRight, ExternalLink, BarChart3,
  TrendingUp, Activity, AlertCircle, Timer, Repeat
} from 'lucide-react';

interface AgentStage {
  id: number;
  name: string;
  image: string;
  oneLiner: string;
  railLabel: string;
}

interface AgentActionVisualizerProps {
  currentAgent: AgentStage;
}

const AgentActionVisualizer: React.FC<AgentActionVisualizerProps> = ({ currentAgent }) => {
  const [activeAction, setActiveAction] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveAction(prev => (prev + 1) % 6);
    }, 1500);

    return () => clearInterval(interval);
  }, [currentAgent.id]);

  const renderLeadGenAgent = () => (
    <div className="space-y-4 animate-popIn" key="leadgen">
      {/* Search Interface */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-3">
          <Search className="w-5 h-5 text-brand-blue animate-pulse" />
          <span className="text-text-primary font-semibold">Searching 12+ Sources</span>
          <div className="ml-auto flex space-x-1">
            <div className="w-2 h-2 bg-brand-green-teal rounded-full animate-pulse" />
            <div className="w-2 h-2 bg-brand-blue rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
            <div className="w-2 h-2 bg-salespad-cyan rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
        </div>
        
        {/* Search Results */}
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border min-h-[44px]">
            <div className="flex items-center space-x-3">
              <Building2 className="w-4 h-4 text-brand-blue" />
              <div>
                <div className="text-text-primary text-xs sm:text-sm font-medium">TechCorp Solutions</div>
                <div className="text-text-secondary text-xs hidden sm:block">Software • 250 employees</div>
              </div>
            </div>
            <CheckCircle className="w-4 h-4 text-brand-green-teal" />
          </div>
          
          <div className="flex items-center justify-between p-2 bg-bg-surface rounded-lg border border-card-border min-h-[44px]">
            <div className="flex items-center space-x-3">
              <Building2 className="w-4 h-4 text-brand-blue" />
              <div>
                <div className="text-text-primary text-xs sm:text-sm font-medium">DataFlow Inc</div>
                <div className="text-text-secondary text-xs hidden sm:block">Analytics • 180 employees</div>
              </div>
            </div>
            <div className="w-4 h-4 border-2 border-yellow-400 rounded-full animate-spin" />
          </div>
        </div>
      </div>

      {/* Contact Verification */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <UserCheck className="w-5 h-5 text-brand-green-teal" />
          <span className="text-text-primary text-sm sm:text-base font-semibold">Contact Verified</span>
        </div>
        
        <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-brand-blue to-brand-green-teal rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-text-primary text-sm sm:text-base font-medium">John Smith</div>
              <div className="text-text-secondary text-xs sm:text-sm">CEO • TechCorp</div>
              <div className="flex items-center space-x-2 sm:space-x-4 mt-1">
                <div className="flex items-center space-x-1">
                  <Phone className="w-3 h-3 text-brand-green-teal" />
                  <span className="text-xs text-brand-green-teal">Verified</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Mail className="w-3 h-3 text-brand-green-teal" />
                  <span className="text-xs text-brand-green-teal hidden sm:inline">Verified</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Linkedin className="w-3 h-3 text-brand-green-teal" />
                  <span className="text-xs text-brand-green-teal hidden sm:inline">Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCallAgent = () => (
    <div className="space-y-4 animate-popIn" key="call">
      {/* Call Interface */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <PhoneCall className="w-5 h-5 text-brand-green-teal animate-pulse" />
          <span className="text-text-primary font-semibold">Live Call in Progress</span>
          <div className="ml-auto flex items-center space-x-2">
            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
            <span className="text-xs text-text-secondary">02:34</span>
          </div>
        </div>
        
        {/* Call Details */}
        <div className="bg-bg-surface rounded-lg p-3 border border-card-border mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-green-teal rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-text-primary font-medium">John Smith</div>
              <div className="text-text-secondary text-sm">CEO • TechCorp Solutions</div>
              <div className="text-brand-green-teal text-xs">+1 (555) 123-4567</div>
            </div>
          </div>
        </div>

        {/* Call Script/Notes */}
        <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
          <div className="text-text-secondary text-xs mb-2">Live Notes:</div>
          <div className="space-y-1">
            <div className="text-text-primary text-sm">✓ Discussed current sales challenges</div>
            <div className="text-text-primary text-sm">✓ Identified pain points with lead gen</div>
            <div className="text-brand-green-teal text-sm animate-pulse">→ Qualifying budget & timeline...</div>
          </div>
        </div>
      </div>

      {/* Calendar Booking */}
      {/* Call Recording */}
      <div className="bg-chip-bg border border-chip-border rounded-lg p-2">
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-5 h-5 bg-red-500 rounded-full animate-pulse flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <span className="text-text-primary font-medium text-sm">Recording</span>
          <div className="ml-auto text-xs text-red-400 font-mono">REC</div>
        </div>
        
        {/* Mini Waveform */}
        <div className="flex items-center space-x-2">
          <div className="flex items-end space-x-0.5 h-3">
            {[40, 70, 50, 80, 60].map((height, index) => (
              <div 
                key={index}
                className="w-1 bg-gradient-to-t from-brand-blue to-brand-green-teal rounded-sm animate-pulse"
                style={{ 
                  height: `${height}%`,
                  animationDelay: `${index * 0.1}s`,
                  animationDuration: '1.5s'
                }}
              />
            ))}
          </div>
          <span className="text-text-secondary text-xs">02:34</span>
          <span className="text-brand-green-teal text-xs ml-auto">Live</span>
        </div>
      </div>
    </div>
  );

  const renderWhatsAppAgent = () => (
    <div className="space-y-4 animate-popIn" key="whatsapp">
      {/* WhatsApp Chat Interface */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <MessageSquare className="w-5 h-5 text-brand-green-teal" />
          <span className="text-text-primary font-semibold">WhatsApp Conversation</span>
          <div className="ml-auto flex items-center space-x-1">
            <div className="w-2 h-2 bg-brand-green-teal rounded-full animate-pulse" />
            <span className="text-xs text-text-secondary">Online</span>
          </div>
        </div>
        
        {/* Chat Messages */}
        <div className="space-y-3 max-h-32 overflow-hidden">
          {/* Outgoing Message */}
          <div className="flex justify-end">
            <div className="bg-brand-blue rounded-lg px-3 py-2 max-w-xs">
              <div className="text-white text-sm">Hi John! Saw your recent post about scaling sales. We help companies like TechCorp automate their entire sales process. Interested in a quick demo?</div>
              <div className="text-blue-200 text-xs mt-1 flex items-center justify-end space-x-1">
                <span>2:34 PM</span>
                <CheckCircle className="w-3 h-3" />
              </div>
            </div>
          </div>
          
          {/* Incoming Message */}
          <div className="flex justify-start">
            <div className="bg-bg-surface border border-card-border rounded-lg px-3 py-2 max-w-xs">
              <div className="text-text-primary text-sm">That sounds interesting! What kind of results do you typically see?</div>
              <div className="text-text-secondary text-xs mt-1">2:36 PM</div>
            </div>
          </div>
          
          {/* Typing Indicator */}
          <div className="flex justify-end">
            <div className="bg-brand-blue/20 border border-brand-blue/30 rounded-lg px-3 py-2">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Analytics */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Eye className="w-5 h-5 text-brand-blue" />
          <span className="text-text-primary font-semibold">Message Analytics</span>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-brand-green-teal font-bold text-lg">98%</div>
            <div className="text-text-secondary text-xs">Delivered</div>
          </div>
          <div className="text-center">
            <div className="text-brand-blue font-bold text-lg">87%</div>
            <div className="text-text-secondary text-xs">Read</div>
          </div>
          <div className="text-center">
            <div className="text-salespad-cyan font-bold text-lg">34%</div>
            <div className="text-text-secondary text-xs">Replied</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailAgent = () => (
    <div className="space-y-4 animate-popIn" key="email">
      {/* Email Composer */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <Mail className="w-5 h-5 text-brand-blue" />
          <span className="text-text-primary font-semibold">Email Campaign</span>
          <div className="ml-auto">
            <Send className="w-4 h-4 text-brand-green-teal animate-pulse" />
          </div>
        </div>
        
        {/* Email Preview */}
        <div className="bg-bg-surface rounded-lg border border-card-border p-3">
          <div className="border-b border-card-border pb-2 mb-2">
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span>To: john.smith@techcorp.com</span>
              <span>High Priority</span>
            </div>
            <div className="text-text-primary text-sm font-medium mt-1">
              Quick question about TechCorp's sales automation
            </div>
          </div>
          
          <div className="text-text-primary text-sm leading-relaxed">
            <div>Hi John,</div>
            <div className="mt-2">I noticed TechCorp recently expanded to 250+ employees. Congrats! With that growth, I imagine your sales team is feeling the pressure to scale their outreach...</div>
            <div className="mt-2 text-brand-blue">Would you be open to a 15-minute demo this week?</div>
          </div>
          
          <div className="mt-3 flex items-center space-x-2">
            <div className="text-xs text-brand-green-teal">✓ Personalized</div>
            <div className="text-xs text-brand-green-teal">✓ A/B Tested</div>
            <div className="text-xs text-brand-green-teal">✓ Optimized Send Time</div>
          </div>
        </div>
      </div>

      {/* Email Performance */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <BarChart3 className="w-5 h-5 text-salespad-cyan" />
          <span className="text-text-primary font-semibold">Campaign Performance</span>
        </div>
        
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center">
            <div className="text-brand-blue font-bold text-sm">2,847</div>
            <div className="text-text-secondary text-xs">Sent</div>
          </div>
          <div className="text-center">
            <div className="text-brand-green-teal font-bold text-sm">68%</div>
            <div className="text-text-secondary text-xs">Opened</div>
          </div>
          <div className="text-center">
            <div className="text-salespad-cyan font-bold text-sm">23%</div>
            <div className="text-text-secondary text-xs">Clicked</div>
          </div>
          <div className="text-center">
            <div className="text-yellow-400 font-bold text-sm">12%</div>
            <div className="text-text-secondary text-xs">Replied</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLinkedInAgent = () => (
    <div className="space-y-4 animate-popIn" key="linkedin">
      {/* LinkedIn Profile View */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <Linkedin className="w-5 h-5 text-brand-blue" />
          <span className="text-text-primary font-semibold">LinkedIn Outreach</span>
          <div className="ml-auto text-xs text-brand-green-teal">Connection Accepted</div>
        </div>
        
        {/* Profile Card */}
        <div className="bg-bg-surface rounded-lg border border-card-border p-3">
          <div className="flex items-start space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-green-teal rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-text-primary font-medium">John Smith</div>
              <div className="text-text-secondary text-sm">CEO at TechCorp Solutions</div>
              <div className="text-text-secondary text-xs">San Francisco Bay Area • 500+ connections</div>
              <div className="flex items-center space-x-2 mt-2">
                <div className="bg-brand-blue/20 text-brand-blue text-xs px-2 py-1 rounded">Software</div>
                <div className="bg-brand-green-teal/20 text-brand-green-teal text-xs px-2 py-1 rounded">CEO</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LinkedIn Message Thread */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <MessageCircle className="w-5 h-5 text-brand-green-teal" />
          <span className="text-text-primary font-semibold">Message Thread</span>
        </div>
        
        <div className="space-y-2">
          <div className="bg-bg-surface rounded-lg p-2 border border-card-border">
            <div className="text-text-primary text-sm">Thanks for connecting, John! I see TechCorp is scaling fast. How are you handling the increased sales volume?</div>
            <div className="text-text-secondary text-xs mt-1">You • 1h ago</div>
          </div>
          
          <div className="bg-brand-blue/10 border border-brand-blue/30 rounded-lg p-2">
            <div className="text-text-primary text-sm">Great question! We're definitely feeling the growing pains. Always looking for better solutions.</div>
            <div className="text-text-secondary text-xs mt-1">John • 23m ago</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFollowUpAgent = () => (
    <div className="space-y-4 animate-popIn" key="followup">
      {/* Follow-up Sequence */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <RotateCcw className="w-5 h-5 text-salespad-cyan animate-pulse" />
          <span className="text-text-primary font-semibold">Follow-up Sequence</span>
          <div className="ml-auto text-xs text-brand-green-teal">Active</div>
        </div>
        
        {/* Sequence Timeline */}
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border">
            <CheckCircle className="w-4 h-4 text-brand-green-teal" />
            <div className="flex-1">
              <div className="text-text-primary text-sm">Initial outreach sent</div>
              <div className="text-text-secondary text-xs">2 days ago</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-brand-blue/30">
            <div className="w-4 h-4 border-2 border-brand-blue rounded-full animate-pulse" />
            <div className="flex-1">
              <div className="text-text-primary text-sm">Follow-up #2 scheduled</div>
              <div className="text-brand-blue text-xs">Sending in 2 hours</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 p-2 bg-bg-surface rounded-lg border border-card-border opacity-60">
            <Clock className="w-4 h-4 text-text-secondary" />
            <div className="flex-1">
              <div className="text-text-secondary text-sm">Follow-up #3</div>
              <div className="text-text-secondary text-xs">In 3 days</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Temperature */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Target className="w-5 h-5 text-yellow-400" />
          <span className="text-text-primary font-semibold">Lead Temperature</span>
        </div>
        
        <div className="bg-bg-surface rounded-lg p-3 border border-card-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
              <span className="text-text-primary text-sm font-medium">Warm Lead</span>
            </div>
            <div className="text-yellow-400 text-sm font-bold">73°</div>
          </div>
          <div className="text-text-secondary text-xs mt-1">Engaged with 2/3 touchpoints</div>
        </div>
      </div>
    </div>
  );

  const renderNurtureAgent = () => (
    <div className="space-y-4 animate-popIn" key="nurture">
      {/* Nurture Campaign Dashboard */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <Activity className="w-5 h-5 text-purple-400 animate-pulse" />
          <span className="text-text-primary font-semibold">Nurture Campaign</span>
          <div className="ml-auto text-xs text-brand-green-teal">Running</div>
        </div>
        
        {/* Campaign Progress */}
        <div className="bg-bg-surface rounded-lg border border-card-border p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-text-primary text-sm font-medium">Q1 Enterprise Campaign</span>
            <span className="text-purple-400 text-xs">Day 14/30</span>
          </div>
          <div className="w-full bg-card-border rounded-full h-2 mb-2">
            <div className="bg-gradient-to-r from-purple-400 to-brand-blue h-2 rounded-full" style={{ width: '47%' }}></div>
          </div>
          <div className="text-text-secondary text-xs">847 prospects in sequence</div>
        </div>

        {/* Recent Activity */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-xs">
            <div className="w-2 h-2 bg-brand-green-teal rounded-full animate-pulse" />
            <span className="text-text-primary">Content shared: "5 Sales Automation Trends"</span>
          </div>
          <div className="flex items-center space-x-2 text-xs">
            <div className="w-2 h-2 bg-brand-blue rounded-full animate-pulse" />
            <span className="text-text-primary">Webinar invite sent to 234 prospects</span>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <TrendingUp className="w-5 h-5 text-brand-green-teal" />
          <span className="text-text-primary font-semibold">Engagement Metrics</span>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-brand-blue font-bold text-lg">73%</div>
            <div className="text-text-secondary text-xs">Open Rate</div>
          </div>
          <div className="text-center">
            <div className="text-purple-400 font-bold text-lg">28%</div>
            <div className="text-text-secondary text-xs">Click Rate</div>
          </div>
          <div className="text-center">
            <div className="text-brand-green-teal font-bold text-lg">156</div>
            <div className="text-text-secondary text-xs">Responses</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBookingAgent = () => (
    <div className="space-y-4 animate-popIn" key="booking">
      {/* Calendar Interface */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <Calendar className="w-5 h-5 text-brand-blue animate-pulse" />
          <span className="text-text-primary font-semibold">Smart Scheduling</span>
          <div className="ml-auto flex items-center space-x-1">
            <div className="w-2 h-2 bg-brand-green-teal rounded-full animate-pulse" />
            <span className="text-xs text-text-secondary">Synced</span>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="bg-bg-surface rounded-lg border border-card-border p-3">
          <div className="text-text-primary text-sm font-medium mb-3">This Week</div>
          <div className="grid grid-cols-5 gap-1 mb-3">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, index) => (
              <div key={day} className="text-center">
                <div className="text-text-secondary text-xs mb-1">{day}</div>
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center text-xs
                  ${index === 1 ? 'bg-brand-blue text-white' : 'bg-card-border text-text-secondary'}
                `}>
                  {12 + index}
                </div>
              </div>
            ))}
          </div>
          
          {/* Time Slots */}
          <div className="space-y-1">
            <div className="flex items-center justify-between p-2 bg-brand-blue/10 border border-brand-blue/30 rounded">
              <span className="text-text-primary text-sm">10:00 AM - Demo Call</span>
              <span className="text-brand-blue text-xs">Confirmed</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-bg-surface border border-card-border rounded">
              <span className="text-text-primary text-sm">2:00 PM - Available</span>
              <span className="text-brand-green-teal text-xs">Open</span>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Analytics */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <CalendarCheck className="w-5 h-5 text-brand-green-teal" />
          <span className="text-text-primary font-semibold">Booking Performance</span>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-brand-blue font-bold text-lg">89%</div>
            <div className="text-text-secondary text-xs">Show Rate</div>
          </div>
          <div className="text-center">
            <div className="text-brand-green-teal font-bold text-lg">47</div>
            <div className="text-text-secondary text-xs">This Week</div>
          </div>
          <div className="text-center">
            <div className="text-salespad-cyan font-bold text-lg">12m</div>
            <div className="text-text-secondary text-xs">Avg Duration</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsAgent = () => (
    <div className="space-y-4 animate-popIn" key="analytics">
      {/* Analytics Dashboard */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4 relative overflow-hidden">
        <div className="flex items-center space-x-3 mb-4">
          <BarChart3 className="w-5 h-5 text-brand-blue animate-pulse" />
          <span className="text-text-primary font-semibold">Sales Analytics</span>
          <div className="ml-auto text-xs text-brand-green-teal">Real-time</div>
        </div>
        
        {/* Performance Chart */}
        <div className="bg-bg-surface rounded-lg border border-card-border p-3 mb-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-text-primary text-sm font-medium">Pipeline Performance</span>
            <span className="text-brand-green-teal text-xs">+23% vs last month</span>
          </div>
          
          {/* Mini Chart Bars */}
          <div className="flex items-end space-x-1 h-16">
            {[40, 65, 45, 80, 70, 90, 85].map((height, index) => (
              <div key={index} className="flex-1 bg-gradient-to-t from-brand-blue to-brand-green-teal rounded-t opacity-80" style={{ height: `${height}%` }} />
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-bg-surface rounded p-2 border border-card-border">
            <div className="text-brand-green-teal font-bold text-sm">$127K</div>
            <div className="text-text-secondary text-xs">Pipeline Value</div>
          </div>
          <div className="bg-bg-surface rounded p-2 border border-card-border">
            <div className="text-brand-blue font-bold text-sm">34%</div>
            <div className="text-text-secondary text-xs">Close Rate</div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-chip-bg border border-chip-border rounded-xl p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Zap className="w-5 h-5 text-yellow-400" />
          <span className="text-text-primary font-semibold">AI Insights</span>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-text-primary text-sm">Best outreach time: 10-11 AM PST</div>
          </div>
          <div className="flex items-start space-x-2">
            <TrendingUp className="w-4 h-4 text-brand-green-teal mt-0.5 flex-shrink-0" />
            <div className="text-text-primary text-sm">Enterprise leads converting 2x faster</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render based on current agent
  switch (currentAgent.name) {
    case 'Lead Gen Agent':
      return renderLeadGenAgent();
    case 'Call Agent':
      return renderCallAgent();
    case 'WhatsApp Agent':
      return renderWhatsAppAgent();
    case 'Email Agent':
      return renderEmailAgent();
    case 'LinkedIn Agent':
      return renderLinkedInAgent();
    case 'Follow Up Agent':
      return renderFollowUpAgent();
    case 'Nurture Agent':
      return renderNurtureAgent();
    case 'Booking Agent':
      return renderBookingAgent();
    case 'Analytics Agent':
      return renderAnalyticsAgent();
    default:
      return renderFollowUpAgent();
  }
};

export default AgentActionVisualizer;